import {
  integrationGoogleFaviconUrl,
  normalizeBrandDomain,
} from '@/lib/integrations/integration-brand-domains'

/**
 * Domain-keyed logo URLs for UI display, served by context.dev Logo Link.
 *
 * HOTLINK ONLY. Logo Link responses are image binaries with a 24h CDN cache
 * and a generated SVG monogram for unknown domains. Their terms forbid
 * downloading/re-hosting the images, and the monogram means the endpoint
 * 200s for ANY domain — so these URLs must NEVER be persisted, rehosted to
 * cdn.brew.new, or used in agent/extraction paths that need real-vs-miss
 * discrimination (those use the Brand API via `retrieveBrand`; see
 * lib/tools/fetch-logo.ts).
 *
 * `theme` follows Logo Link semantics: the UI theme the logo is embedded
 * into (`light` → dark-artwork logo, `dark` → light-artwork logo). Note this
 * is the OPPOSITE of the repo's stored-logo `theme` convention, which names
 * the color of the logo artwork itself (see `getLogoForTheme`).
 */
export function getClientBrandLogoUrl(
  domain: string,
  opts?: { theme?: 'light' | 'dark' }
): string {
  const clean = normalizeBrandDomain(domain)
  if (!clean) {
    return ''
  }
  // Read at call time (repo convention; Next.js still inlines the
  // NEXT_PUBLIC_* expression into client bundles at build).
  const clientId = process.env.NEXT_PUBLIC_CONTEXT_DEV_PUBLIC_CLIENT_ID ?? ''
  if (clientId) {
    const params = new URLSearchParams({
      publicClientId: clientId,
      domain: clean,
    })
    if (opts?.theme) {
      params.set('theme', opts.theme)
    }
    return `https://logos.context.dev/?${params.toString()}`
  }
  return integrationGoogleFaviconUrl(clean, 128)
}

export function getClientBrandFaviconUrl(domain: string): string {
  const clean = normalizeBrandDomain(domain)
  if (!clean) {
    return ''
  }
  return integrationGoogleFaviconUrl(clean, 128)
}
